import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, CheckCircle2, Circle, Loader2, X } from 'lucide-react';

import type {
  ReplenishmentRefreshModalMode,
  ReplenishmentRefreshStep,
  ReplenishmentRefreshStepId,
} from '../hooks/useReplenishmentRefreshFlow';

interface Props {
  open: boolean;
  mode: ReplenishmentRefreshModalMode;
  steps: ReplenishmentRefreshStep[];
  isRunning: boolean;
  flowComplete: boolean;
  partialError: string | null;
  replenishmentSucceeded: boolean;
  onConfirm: () => void;
  onClose: () => void;
  onRetry: () => void;
}

function StepIcon({ status }: { status: ReplenishmentRefreshStep['status'] }) {
  if (status === 'running') {
    return <Loader2 className="h-5 w-5 shrink-0 animate-spin text-stocker-primary" />;
  }
  if (status === 'done') {
    return <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />;
  }
  if (status === 'error') {
    return <AlertCircle className="h-5 w-5 shrink-0 text-red-600" />;
  }
  return <Circle className="h-5 w-5 shrink-0 text-gray-300" />;
}

export function ReplenishmentRefreshProgressModal({
  open,
  mode,
  steps,
  isRunning,
  flowComplete,
  partialError,
  replenishmentSucceeded,
  onConfirm,
  onClose,
  onRetry,
}: Props) {
  const { t } = useTranslation();

  if (!open) return null;

  const stepLabel = (id: ReplenishmentRefreshStepId) =>
    t(`forecastReview.alerts.replenishmentRefresh.steps.${id}`);

  const showRetry =
    !isRunning &&
    steps.some(
      (step) =>
        (step.id === 'replenishment' && step.status === 'error') ||
        (step.id === 'supply_cache' && step.status === 'error' && replenishmentSucceeded),
    );

  const content = (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-[1px]"
        onClick={isRunning ? undefined : onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="replenishment-refresh-modal-title"
        className="relative w-full max-w-md rounded-xl border border-gray-200 bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h2 id="replenishment-refresh-modal-title" className="text-base font-semibold text-gray-900">
              {mode === 'confirm'
                ? t('forecastReview.alerts.replenishmentRefresh.confirmTitle')
                : flowComplete
                  ? t('forecastReview.alerts.replenishmentRefresh.completedTitle')
                  : t('forecastReview.alerts.replenishmentRefresh.progressTitle')}
            </h2>
            {mode === 'confirm' ? (
              <p className="mt-1 text-sm text-gray-600">
                {t('forecastReview.alerts.replenishmentRefresh.confirmMessage')}
              </p>
            ) : (
              <p className="mt-1 text-sm text-gray-500">
                {t('forecastReview.alerts.replenishmentRefresh.progressHint')}
              </p>
            )}
          </div>
          {!isRunning ? (
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label={t('forecastReview.alerts.replenishmentRefresh.close')}
            >
              <X className="h-5 w-5" />
            </button>
          ) : null}
        </div>

        {mode === 'confirm' ? (
          <div className="flex justify-end gap-2 px-5 py-4">
            <button
              type="button"
              className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
              onClick={onClose}
            >
              {t('forecastReview.alerts.replenishmentRefresh.cancel')}
            </button>
            <button
              type="button"
              className="rounded-md bg-stocker-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90"
              onClick={() => void onConfirm()}
            >
              {t('forecastReview.alerts.refreshSupply')}
            </button>
          </div>
        ) : (
          <div className="px-5 py-4">
            <ul className="space-y-3">
              {steps.map((step) => (
                <li key={step.id} className="flex items-start gap-3">
                  <StepIcon status={step.status} />
                  <div className="min-w-0 flex-1">
                    <p
                      className={`text-sm font-medium ${
                        step.status === 'error' ? 'text-red-700' : 'text-gray-900'
                      }`}
                    >
                      {stepLabel(step.id)}
                    </p>
                    {step.status === 'error' && step.errorMessage ? (
                      <p className="mt-0.5 text-xs text-red-600">{step.errorMessage}</p>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>

            {partialError && replenishmentSucceeded ? (
              <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                {t('forecastReview.alerts.replenishmentRefresh.partialError')}
              </div>
            ) : null}

            {flowComplete ? (
              <div className="mt-4 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                {t('forecastReview.alerts.replenishmentRefresh.completedMessage')}
              </div>
            ) : null}

            <div className="mt-5 flex flex-wrap justify-end gap-2">
              {showRetry ? (
                <button
                  type="button"
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
                  onClick={() => void onRetry()}
                >
                  {t('forecastReview.alerts.replenishmentRefresh.retry')}
                </button>
              ) : null}
              {!isRunning && (flowComplete || partialError) ? (
                <button
                  type="button"
                  className="rounded-md bg-stocker-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90"
                  onClick={onClose}
                >
                  {t('forecastReview.alerts.replenishmentRefresh.close')}
                </button>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
